/** "This month" digest card — family anniversaries + hometown headlines. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarHeart, Newspaper, ExternalLink, MailCheck, MapPin, MessageCircle, X, Send } from "lucide-react";
import api from "@/lib/api";

/** Build the digest share text. */
function digestText(digest) {
  const lines = [`Your family month — ${digest.month_label}`];
  if (digest.anniversaries?.length) {
    lines.push("", "This month your family remembers:");
    digest.anniversaries.forEach((a) =>
      lines.push(`• ${a.name} — ${a.kind === "born" ? "born" : "passed away"} ${a.date}`)
    );
  }
  if (digest.headlines?.length && digest.place?.city) {
    lines.push("", `From ${digest.place.city}, where your story lives:`);
    digest.headlines.forEach((h) => lines.push(`• ${h.title}\n  ${h.link}`));
  }
  lines.push("", "— FamilyTree.Team");
  return lines.join("\n");
}

export default function DigestCard() {
  const [digest, setDigest] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [waOpen, setWaOpen] = useState(false);
  const [waNumber, setWaNumber] = useState(() => {
    try { return localStorage.getItem("ft_wa_number") || ""; } catch { return ""; }
  });

  useEffect(() => {
    api
      .get("/v1/digest")
      .then(({ data }) => setDigest(data.digest))
      .catch(() => setDigest(null))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || !digest) return null;
  const { anniversaries = [], headlines = [], place } = digest;
  if (anniversaries.length === 0 && headlines.length === 0) return null;

  return (
    <section className="border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-6 md:p-8 mb-14" data-testid="digest-card">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="overline text-[color:var(--sepia)] flex items-center gap-2">
          <CalendarHeart size={13} /> This month · {digest.month_label}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setWaOpen(true)}
            data-testid="digest-whatsapp"
            className="inline-flex items-center gap-1.5 border border-[color:var(--sepia)]/40 px-3 py-1.5 text-xs text-[color:var(--sepia)] hover:bg-[color:var(--parchment-2)] transition-colors"
          >
            <MessageCircle size={12} /> Send to family WhatsApp
          </button>
          {digest.emailed && (
            <span className="inline-flex items-center gap-1 numeric-index text-[color:var(--ink-soft)]" data-testid="digest-emailed">
              <MailCheck size={11} /> sent to your inbox
            </span>
          )}
        </div>
      </div>

      {/* WhatsApp send popup — enter a number, WhatsApp opens with the digest ready to send */}
      {waOpen && (
        <div className="mt-4 border border-[color:var(--sepia)]/30 bg-[color:var(--parchment-2)]/50 p-4" data-testid="digest-wa-popup">
          <div className="flex items-start justify-between gap-3">
            <p className="text-sm text-[color:var(--ink-soft)]">
              Enter the family WhatsApp number (with country code, e.g. 919812345678). WhatsApp will open with the digest written — just press send.
            </p>
            <button onClick={() => setWaOpen(false)} className="p-1 hover:text-[color:var(--rust)]" data-testid="digest-wa-close"><X size={14} /></button>
          </div>
          <form
            className="mt-3 flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              const num = waNumber.replace(/[^\d]/g, "");
              if (num.length < 8) return;
              try { localStorage.setItem("ft_wa_number", num); } catch { /* ignore */ }
              const base = num ? `https://wa.me/${num}` : "https://wa.me/";
              window.open(`${base}?text=${encodeURIComponent(digestText(digest))}`, "_blank", "noopener");
              setWaOpen(false);
            }}
          >
            <input
              data-testid="digest-wa-number"
              value={waNumber}
              onChange={(e) => setWaNumber(e.target.value)}
              placeholder="919812345678"
              inputMode="tel"
              className="flex-1 bg-transparent border-b border-[color:var(--sepia)]/50 py-2 text-sm focus:outline-none focus:border-[color:var(--rust)]"
            />
            <button type="submit" data-testid="digest-wa-send" className="btn-wood !py-2 !px-4 !text-xs">
              <Send size={12} /> Open WhatsApp
            </button>
            <button
              type="button"
              data-testid="digest-wa-group"
              onClick={() => {
                window.open(`https://wa.me/?text=${encodeURIComponent(digestText(digest))}`, "_blank", "noopener");
                setWaOpen(false);
              }}
              className="btn-ghost !py-2 !px-3 !text-xs"
            >
              Choose a group instead
            </button>
          </form>
        </div>
      )}

      <div className={`mt-5 grid gap-8 ${anniversaries.length > 0 && headlines.length > 0 ? "md:grid-cols-2" : ""}`}>
        {anniversaries.length > 0 && (
          <div data-testid="digest-anniversaries">
            <div className="overline text-[10px] text-[color:var(--ink-soft)] mb-3">Your family remembers</div>
            <ul className="space-y-2.5">
              {anniversaries.map((a, i) => (
                <li key={i} data-testid={`digest-anniversary-${i}`} className="flex items-baseline justify-between gap-3 border-b border-[color:var(--sepia)]/15 pb-2">
                  <Link to={`/app/memorial/${a.person_id}`} className="font-serif-display text-lg link-underline">
                    {a.name}
                  </Link>
                  <span className="text-xs text-[color:var(--ink-soft)] shrink-0">
                    {a.kind === "born" ? "born" : "passed"} · {a.date}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {headlines.length > 0 && place?.city && (
          <div data-testid="digest-headlines">
            <div className="overline text-[10px] text-[color:var(--ink-soft)] mb-3 flex items-center gap-1">
              <MapPin size={10} /> From {place.city}, where your story lives
            </div>
            <ul className="space-y-2.5">
              {headlines.map((h, i) => (
                <li key={i} data-testid={`digest-headline-${i}`}>
                  <a
                    href={h.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-baseline gap-2 text-[15px] leading-snug"
                  >
                    <Newspaper size={12} className="shrink-0 text-[color:var(--sepia)] translate-y-px" />
                    <span className="group-hover:text-[color:var(--rust)] transition-colors">{h.title}</span>
                    <ExternalLink size={11} className="shrink-0 text-[color:var(--sepia)]/50" />
                  </a>
                </li>
              ))}
            </ul>
            <Link to="/local" className="mt-3 inline-block text-xs link-underline text-[color:var(--rust)]" data-testid="digest-local-link">
              Open your local page
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
